export type ChatPoint = { x: number; y: number };
export type ChatBounds = { minX: number; maxX: number; minY: number; maxY: number };
export type ChatViewport = {
  width: number;
  height: number;
  left: number;
  top: number;
  safeTop: number;
  safeRight: number;
  safeBottom: number;
  safeLeft: number;
};

export const CHAT_POSITION_KEY = 'jlbhk_chat_position_v1';
export const DEFAULT_CHAT_POSITION: ChatPoint = { x: 1, y: 1 };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function chatBounds(viewport: ChatViewport, size: number): ChatBounds {
  const margin = 16;
  const minX = viewport.left + viewport.safeLeft + margin;
  const minY = viewport.top + viewport.safeTop + margin;
  return {
    minX,
    minY,
    maxX: Math.max(minX, viewport.left + viewport.width - viewport.safeRight - size - margin),
    maxY: Math.max(minY, viewport.top + viewport.height - viewport.safeBottom - size - margin),
  };
}

export function clampChatPoint(point: ChatPoint, bounds: ChatBounds): ChatPoint {
  return {
    x: clamp(point.x, bounds.minX, bounds.maxX),
    y: clamp(point.y, bounds.minY, bounds.maxY),
  };
}

export function chatPointFromRatio(ratio: ChatPoint, bounds: ChatBounds): ChatPoint {
  return clampChatPoint({
    x: bounds.minX + clamp(ratio.x, 0, 1) * (bounds.maxX - bounds.minX),
    y: bounds.minY + clamp(ratio.y, 0, 1) * (bounds.maxY - bounds.minY),
  }, bounds);
}

export function chatPointToRatio(point: ChatPoint, bounds: ChatBounds): ChatPoint {
  const safePoint = clampChatPoint(point, bounds);
  return {
    x: bounds.maxX === bounds.minX ? 1 : (safePoint.x - bounds.minX) / (bounds.maxX - bounds.minX),
    y: bounds.maxY === bounds.minY ? 1 : (safePoint.y - bounds.minY) / (bounds.maxY - bounds.minY),
  };
}

export function parseChatPosition(value: string | null): ChatPoint | null {
  if (!value) return null;
  try {
    const position: unknown = JSON.parse(value);
    if (!position || typeof position !== 'object') return null;
    const record = position as Record<string, unknown>;
    if (record.version !== 1 || typeof record.x !== 'number' || typeof record.y !== 'number') return null;
    if (!Number.isFinite(record.x) || !Number.isFinite(record.y)) return null;
    return { x: clamp(record.x, 0, 1), y: clamp(record.y, 0, 1) };
  } catch {
    return null;
  }
}
